// Signed-in layout: navy sidebar, top bar (global search, company, user menu), page outlet.
import { useQuery } from '@tanstack/react-query'
import { Outlet, useRouterState } from '@tanstack/react-router'
import { Menu, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog'
import { api } from '../lib/api'
import { AuthProvider, useAuth } from '../lib/auth-context'
import { COMPANY_QUERY, normalizeList, paths } from '../lib/endpoints'
import { canReadModule, isEmployeeOnly } from '../lib/permissions'
import { CommandPalette } from './CommandPalette'
import { Sidebar } from './Sidebar'
import { UserMenu } from './UserMenu'

/** The company name for the header: the company singleton's oldest row, the one every module reads. */
export async function fetchCompanyName(signal?: AbortSignal): Promise<string | null> {
  const res = normalizeList(await api.get(paths.list('companies'), { query: COMPANY_QUERY, signal }))
  const name = res.data[0]?.name
  return typeof name === 'string' && name.trim() ? name : null
}

function useCompanyName(): string {
  const { me, viewer } = useAuth()
  const readable = canReadModule(viewer, 'settings')
  const { data } = useQuery({
    queryKey: ['records', 'company', 'name'],
    queryFn: ({ signal }) => fetchCompanyName(signal),
    enabled: readable,
    staleTime: 10 * 60_000,
  })
  return data ?? me.companyName ?? 'Clearpol'
}

function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
}

function Shell() {
  const { viewer } = useAuth()
  const company = useCompanyName()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [mobileNav, setMobileNav] = useState(false)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const employeeOnly = isEmployeeOnly(viewer)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on every navigation (pathname change) on purpose.
  useEffect(() => {
    setMobileNav(false)
    // The page scrolls inside <main>, not the window, so reset it on navigation.
    document.getElementById('main')?.scrollTo({ top: 0 })
  }, [pathname])

  return (
    <div className="flex h-full min-h-0">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:shadow"
      >
        Skip to content
      </a>
      <Sidebar className="hidden shrink-0 lg:flex" />
      <Dialog open={mobileNav} onOpenChange={setMobileNav}>
        <DialogContent
          hideClose
          className="top-0 left-0 h-full max-h-full w-60 max-w-60 translate-x-0 rounded-none border-0 p-0"
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">Navigation</DialogTitle>
          <Sidebar onNavigate={() => setMobileNav(false)} />
        </DialogContent>
      </Dialog>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-white px-4 lg:px-6">
          <button
            type="button"
            className="rounded-md p-1.5 text-ink-muted hover:bg-canvas lg:hidden"
            aria-label="Open navigation"
            onClick={() => setMobileNav(true)}
          >
            <Menu className="size-5" />
          </button>
          {!employeeOnly ? (
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="flex h-9 w-full max-w-md items-center gap-2 rounded-md border border-line bg-canvas px-3 text-left text-sm text-ink-subtle hover:border-line-strong focus-visible:outline-2 focus-visible:outline-brand-500"
              aria-keyshortcuts="Control+K Meta+K"
            >
              <Search className="size-4" aria-hidden />
              <span className="flex-1 truncate">Search Clearpol Compliance</span>
              <kbd className="hidden rounded border border-line bg-white px-1.5 font-sans text-[11px] text-ink-subtle sm:inline">
                {isMac() ? 'Cmd' : 'Ctrl'} K
              </kbd>
            </button>
          ) : (
            <div className="flex-1" />
          )}
          <div className="ml-auto flex items-center gap-4">
            <span className="hidden text-sm font-medium text-ink-muted sm:inline">{company}</span>
            <UserMenu />
          </div>
        </header>
        <main id="main" className="min-h-0 flex-1 overflow-y-auto" tabIndex={-1}>
          <div className="mx-auto w-full max-w-[1440px] px-4 py-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
      {!employeeOnly ? <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} /> : null}
    </div>
  )
}

export function AppLayout() {
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  )
}
